import { ComboboxItem, MultiSelect } from '@mantine/core';
import { ReactNode, useMemo } from 'react';
import { FieldValues, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormControlInput } from '@/core/components';

interface NamedOption {
  id: number;
  name: string;
}

export interface AsyncMultiSelectControlProps<T extends FieldValues> extends FormControlInput<T> {
  withAsterisk?: boolean;

  label: string;
  description?: string;
  placeholder?: string;

  isDisabled?: boolean;

  limit?: number;

  options: NamedOption[];

  isLoading: boolean;
  isError: boolean;

  nothingFoundMessage?: ReactNode;
}

function normalize(op: NamedOption): ComboboxItem {
  return {
    value: String(op.id),
    label: op.name,
  };
}

function byId(options: NamedOption[], id: string): NamedOption | undefined {
  return options.find(op => String(op.id) === id);
}

export function AsyncMultiSelectControl<T extends FieldValues>(props: AsyncMultiSelectControlProps<T>) {
  const {
    withAsterisk,
    label,
    placeholder,
    description,
    name,
    isSubmitting,
    isDisabled,
    limit,
    options,
    isError,
    isLoading,
    nothingFoundMessage,
    control,
  } = props;
  const { t } = useTranslation();
  const {
    field,
    fieldState: { error },
  } = useController({ name, control });

  const selected: NamedOption[] = useMemo(() => field.value ?? [], [field.value]);

  // A selected entry the loaded page does not contain still needs an option, or Mantine labels its pill with the raw id.
  const normalizedOptions = useMemo(() => {
    const loaded = options ?? [];
    const unresolved = selected.filter(entry => byId(loaded, String(entry.id)) == null);

    return [...loaded, ...unresolved].map(normalize);
  }, [options, selected]);

  const normalizedOnChange = (ids: string[]) => {
    field.onChange(
      ids.map(id => byId(options ?? [], id) ?? byId(selected, id)).filter((op): op is NamedOption => op != null),
    );
  };

  const normalizedValue = field.value != null ? field.value.map((v: NamedOption) => String(v.id)) : null;

  return (
    <MultiSelect
      data={normalizedOptions}
      ref={field.ref}
      name={field.name}
      value={normalizedValue ?? []}
      onChange={normalizedOnChange}
      onDropdownClose={field.onBlur}
      label={label}
      description={description}
      placeholder={placeholder ?? label}
      limit={limit}
      withAsterisk={withAsterisk}
      error={error?.message ?? (isError ? t('label.serverError') : null)}
      disabled={isSubmitting || isDisabled || isLoading || isError}
      nothingFoundMessage={nothingFoundMessage}
      searchable
      flex={1}
      withScrollArea={false}
      styles={{ dropdown: { maxHeight: 200, overflowY: 'auto' } }}
    />
  );
}
